import React, { useEffect, useState } from "react";
import AlertComponent from "components/Alert";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { ApprovalAction, ApprovalTaskStatues, Roles } from "common";
import { useGetProfileQuery } from "redux/services/membership";
import { useUpdateApprovalTaskMutation } from "redux/services/approvalProcessor";
import { getOrganisationMembership } from "utils";

interface Props {
  isOpen: boolean;
  type?: ApprovalTaskStatues;
  approvalTask?: any;
  onClose: () => void;
  onSuccess?: () => void;
}

const ProcessDeclineDialog = ({
  isOpen,
  type,
  approvalTask,
  onClose,
  onSuccess,
}: Props) => {
  const toast = useToast();
  const cancelRef = React.useRef(null);
  const [commentValue, setCommentValue] = useState<string>();
  const [updateApprovalTask, { isLoading, isSuccess, isError, error }] =
    useUpdateApprovalTaskMutation();

  const { data: ProfileData } = useGetProfileQuery();
  const membershipOrganisation = getOrganisationMembership(
    ProfileData?.data?.memberships
  );

  const isPaymentOpsRole = ProfileData?.data?.listRoles?.includes(
    Roles.PaymentOps
  );
  const isBusinessApproverRole = ProfileData?.data?.listRoles?.includes(
    Roles.BusinessApprover
  );
  const isBUApproverRole = ProfileData?.data?.listRoles?.includes(
    Roles.BUApprover
  );

  const renderTitleAction = () => {
    switch (true) {
      case isPaymentOpsRole && type === ApprovalTaskStatues.APPROVED:
        return "Process";
      case isPaymentOpsRole && type === ApprovalTaskStatues.REJECTED:
        return "Decline";
      case isBusinessApproverRole && type === ApprovalTaskStatues.APPROVED:
        return "Approve";
      case (isBusinessApproverRole || isBUApproverRole) && type === ApprovalTaskStatues.REJECTED:
        return "Reject";
      default:
        return "";
    }
  };

  const handleSubmit = () => {
    const body = {
      orgToken: membershipOrganisation?.token,
      id: approvalTask?.referenceNumber,
      status: type,
      actorId: ProfileData?.data?.userId,
      comment: commentValue,
    };
    updateApprovalTask(body);
  };

  useEffect(() => {
    if (isSuccess) {
      let actionTitle;
      switch (true) {
        case isPaymentOpsRole && type === ApprovalTaskStatues.APPROVED:
          actionTitle = ApprovalAction.PROCESSED;
          break;
        case isPaymentOpsRole && type === ApprovalTaskStatues.REJECTED:
          actionTitle = ApprovalAction.DECLINED;
          break;
        case isBusinessApproverRole && type === ApprovalTaskStatues.APPROVED:
          actionTitle = ApprovalAction.APPROVED;
          break;
        case isBusinessApproverRole && type === ApprovalTaskStatues.REJECTED:
          actionTitle = ApprovalAction.REJECTED;
          break;
        case isBUApproverRole && type === ApprovalTaskStatues.APPROVED:
          actionTitle = ApprovalAction.APPROVED;
          break;
        case isBUApproverRole && type === ApprovalTaskStatues.REJECTED:
          actionTitle = ApprovalAction.REJECTED;
          break;
        default:
          actionTitle = "";
          break;
      }

      onSuccess?.();
      onClose?.();

      toast({
        title: `Payment successfully ${actionTitle}.`,
        description: "",
        status: type === ApprovalTaskStatues.APPROVED ? "success" : "error",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  useEffect(() => {
    if (isOpen) setCommentValue("");
  }, [isOpen]);

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={isLoading ? () => { } : onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {renderTitleAction()} Payment
          </AlertDialogHeader>

          <AlertDialogBody>
            {isError && (
              <AlertComponent
                data-testid="alert-error"
                status="error"
                mb="10px"
                title={
                  (error as any)?.data?.errors
                    ?.map((err: any) => err.message)
                    ?.join(", ") ?? "Something went wrong!"
                }
              />
            )}

            {commentValue && commentValue.length >= 250 && (
              <AlertComponent
                data-testid="alert-error"
                status="error"
                mb="10px"
                title="Comment must be less than 250 characters"
              />
            )}

            <Text fontSize="14px" lineHeight="1.5">
              Are you sure you want to{` `}
              {renderTitleAction().toLowerCase()}
              {` `}this payment? This cannot be undone.
            </Text>

            <Textarea
              mt="16px"
              fontSize="14px"
              placeholder="Add a comment"
              value={commentValue}
              onChange={(e) => setCommentValue(e.target.value)}
              isInvalid={!!(commentValue && commentValue?.length >= 250)}
            />
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              ml={3}
              disabled={
                isLoading ||
                (type === ApprovalTaskStatues.REJECTED && !commentValue) ||
                !!((type === ApprovalTaskStatues.APPROVED || type === ApprovalTaskStatues.REJECTED) && commentValue && commentValue?.length >= 250)
              }
            >
              Confirm
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default ProcessDeclineDialog;
